import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { setupCopy, setupMedia } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const SetupSection = () => {
  return (
    <section data-testid="setup-section" className="px-6 lg:px-10 py-6 lg:py-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="grid grid-cols-2 overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-dark)]"
      >
        <div className="flex flex-col justify-center p-10 lg:p-16">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FB89E]">
            {setupCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[34px] lg:text-[46px] font-semibold leading-[1.08] tracking-[-0.03em] text-[var(--strollee-bg)]">
            {setupCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
          <p className="mt-5 max-w-[420px] text-base lg:text-lg leading-relaxed text-[#A3A199]">
            {setupCopy.support}
          </p>

          <div className="mt-9 max-w-[440px]">
            {setupCopy.steps.map((step, i) => (
              <motion.div
                key={step}
                data-testid={`setup-step-${i}`}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, ease: EASE, delay: 0.3 + i * 0.15 }}
                className="flex items-center gap-4 border-t border-white/12 py-3.5"
              >
                <span className="text-[12px] font-semibold tracking-[0.18em] text-white/40">
                  0{i + 1}
                </span>
                <span className="text-[15px] font-medium text-[#EDEAE3]">
                  {step}
                </span>
              </motion.div>
            ))}
            <motion.div
              data-testid="setup-step-result"
              initial={{ opacity: 0, x: -14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, ease: EASE, delay: 0.3 + setupCopy.steps.length * 0.15 }}
              className="flex items-center gap-4 border-y border-white/12 py-3.5"
            >
              <ArrowRight size={15} strokeWidth={2.2} className="text-[#8FB89E]" />
              <span className="text-[15px] font-semibold text-[#8FB89E]">
                {setupCopy.resultLabel}
              </span>
            </motion.div>
          </div>

          <div className="mt-9 flex items-center gap-6">
            <a
              href="#find-my-setup"
              data-testid="setup-primary-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[var(--strollee-surface)] px-7 py-3.5 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-all duration-200 hover:bg-white hover:shadow-[0_8px_28px_rgba(0,0,0,0.35)] hover:-translate-y-[2px]"
            >
              {setupCopy.primaryCta}
              <ArrowRight
                size={16}
                strokeWidth={2.2}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </a>
            <p className="text-[13px] text-white/50">
              {setupCopy.secondaryHint}{" "}
              <a
                href="#build"
                data-testid="setup-secondary-cta"
                className="group inline-flex items-center gap-1 font-semibold text-white/85 transition-colors duration-200 hover:text-[#8FB89E]"
              >
                {setupCopy.secondaryCta}
                <ArrowUpRight
                  size={14}
                  strokeWidth={2.4}
                  className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                />
              </a>
            </p>
          </div>
        </div>

        <div className="relative min-h-[520px] lg:min-h-[600px]">
          <img
            src={setupMedia.url}
            alt={setupMedia.alt}
            data-testid="setup-media"
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--strollee-dark)]/45 via-transparent to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};
